import {
  date
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { agentActions, agentConfigurations, agentRuns, aiCorrections, aiDocumentExamples, aiDraftEdits, aiDrafts, aiFeedback, aiInteractionLogs, aiReviews, aiSuggestionFeedback, aiTrainingFeedback, damageAnalysisCorrections, damageAssessments, damageTrainingDataset, discrepancyAnnotations, fineTuningJobs, learningExamples, policyClassificationTraining, reviewFeedback, settlementPredictions, smartSuggestions, strategyOutcomes, timelineNotePatterns, trainingData, trainingExportJobs, vertexAiModels } from './ai';
import { appraisalAgreements, appraisalAssignments, appraisalDecisions, appraisalTracking, appraisalWorkflowDocuments, externalAppraisers } from './appraisal';
import { adjusterPhoneDirectory, callRecords, companyPhoneLines } from './calls';
import { carriers } from './carriers';
import { chatAgents, chatMessageReactions, chatMessages, chatParticipants, chatSpaces, pendingToolCalls } from './chat';
import { repairFollowupEvents, repairFollowupWatch } from './claims-repair';
import { attorneyExclusions, claimActivityTracking, claimAdjusters, claimComplianceItems, claimCoverages, claimLinks, claimPaAssignments, claimPackageGenerations, claims, claimsEvaluations, contractors, inspections, intakeDecisions, policyAnalysis, policyholderUpdates, questionnaireResponses, questionnaireTemplates, timelineEvents } from './claims';
import { automatedCommunications, backgroundJobs, communicationSummaries, communications, emailAccounts, emailAddressHistory, emailCommunications, emailDrafts, emailTemplates, processedEmails } from './communications';
import { breachNotifications, dataDeletionRequests, firmLicenses, retentionPolicies, stateComplianceRules, stateLicenses, stateLicensingRequirements, stateStatutes } from './compliance';
import { contacts, extractedContacts, extractedDeadlines } from './contacts';
import { apiKeys, auditLogs, AUDIT_ACTIONS, AUDIT_ENTITIES, companySettings, orgUsageLimits, systemSettings } from './core';
import { completionDocuments, documentChunks, documentContentLearning, documentGenerations, documentTemplatePackages, documentTemplates, documents, folders, packageTemplates, packageValidationRules, packageVersions } from './documents';
import { docusignEnvelopes } from './docusign';
import { carrierChecks, carrierOffers, checkEstimateBreakdowns, commissionPayments, commissions, companyExpenses, estimateComparisons, estimateComponentGroups, estimateItemObservations, estimatePitfallPatterns, estimateSamples, estimates, feeConfirmations, invoiceLineItems, invoices, paymentCoverageBreakdowns, payments, receivableFees, staffTime } from './financial';
import { leadFeedbacks, leadPaAssignments, leads } from './leads';
import { notifications } from './notifications';
import { claimPhotoComments, claimPhotos } from './photos';
import { quickbooksAccountMappings, quickbooksSyncLog, quickbooksTokens } from './qbo';
import { knowledgeBaseEntries, searchAnalytics, searchEmbeddings, tokenDefinitions } from './rag';
import { savedReports } from './reports';
import { reminderConfigurations, reminderHistory, taskAssignees, tasks } from './tasks';
import { permissions, rolePermissions, roles, userPermissionOverrides, userSettings, users, userLoginLogs } from './users';
import { oauthStates } from './oauth';
import { importJobGroups, importJobItems, importJobs } from './import-jobs';
import { timesheets } from './timesheets';
import { letterstreamJobs } from './postal';

export const insertFolderSchema = createInsertSchema(folders).omit({
  createdAt: true,
  updatedAt: true,
});


export type InsertFolder = z.infer<typeof insertFolderSchema>;

export type SelectFolder = typeof folders.$inferSelect;


// System Settings insert schemas
export const insertSystemSettingsSchema = createInsertSchema(systemSettings).omit({
  updatedAt: true,
});


export type InsertSystemSettings = z.infer<typeof insertSystemSettingsSchema>;

export type SystemSettings = typeof systemSettings.$inferSelect;


// Company Settings insert schemas
export const insertCompanySettingsSchema = createInsertSchema(companySettings).omit({
  id: true,
  updatedAt: true,
});


export type InsertCompanySettings = z.infer<typeof insertCompanySettingsSchema>;

export type CompanySettings = typeof companySettings.$inferSelect;


export const insertFirmLicenseSchema = createInsertSchema(firmLicenses).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});


export type InsertFirmLicense = z.infer<typeof insertFirmLicenseSchema>;

export type FirmLicense = typeof firmLicenses.$inferSelect;


export const insertStateLicensingRequirementSchema = createInsertSchema(stateLicensingRequirements).omit({
  id: true,
  lastUpdated: true,
});


export type InsertStateLicensingRequirement = z.infer<typeof insertStateLicensingRequirementSchema>;

export type StateLicensingRequirement = typeof stateLicensingRequirements.$inferSelect;


export const insertRepairFollowupWatchSchema = createInsertSchema(repairFollowupWatch).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});


export type InsertRepairFollowupWatch = z.infer<typeof insertRepairFollowupWatchSchema>;

export type RepairFollowupWatch = typeof repairFollowupWatch.$inferSelect;


export const insertRepairFollowupEventSchema = createInsertSchema(repairFollowupEvents).omit({
  id: true,
  createdAt: true,
});


export type InsertRepairFollowupEvent = z.infer<typeof insertRepairFollowupEventSchema>;

export type RepairFollowupEvent = typeof repairFollowupEvents.$inferSelect;


// User Settings insert schemas
export const insertUserSettingsSchema = createInsertSchema(userSettings).omit({
  createdAt: true,
  updatedAt: true,
});


export type InsertUserSettings = z.infer<typeof insertUserSettingsSchema>;

export type UserSettings = typeof userSettings.$inferSelect;


export type OAuthState = typeof oauthStates.$inferSelect;

export type InsertOAuthState = typeof oauthStates.$inferInsert;


export const insertEstimateComparisonSchema = createInsertSchema(estimateComparisons).omit({
  id: true,
  createdAt: true,
});

export type InsertEstimateComparison = z.infer<typeof insertEstimateComparisonSchema>;

export type EstimateComparisonDB = typeof estimateComparisons.$inferSelect;


// Insert schemas
export const insertClaimSchema = createInsertSchema(claims).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  // Enhanced date of loss validation (date-only string)
  dateOfLoss: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD").refine((str) => {
    const today = new Date().toISOString().split('T')[0];
    return str <= today;
  }, "Date of loss cannot be in the future"),
  
  // Claim number validation (optional - can be added later from carrier paperwork)
  claimNumber: z.string().max(50).optional().nullable(),
  
  // Policy number validation (optional - can be added later from carrier paperwork)
  policyNumber: z.string().max(50).optional().nullable(),
  
  // Optional date-only fields (YYYY-MM-DD strings)
  claimFiledDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD").nullable().optional(),
  carrierPaperworkDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD").nullable().optional(),
  proofOfLossSubmittedDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD").nullable().optional(),
  carrierResponseDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD").nullable().optional(),
  depreciationRecoveryDeadline: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD").nullable().optional(),
  statuteLimitationsDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD").nullable().optional(),
  appraisalDeadlineDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD").nullable().optional(),
  mplcEngagementDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD").nullable().optional(),
  referrerId: z.string().uuid().optional().nullable(),
}).refine((data) => {
  // A claim can be insured by a business OR an individual, so require either a
  // company name OR a first + last name. A commercial claim may be insured by
  // an individual person, so it accepts a personal name too (GitHub #399).
  const hasCompany = !!(data.policyholderCompany && data.policyholderCompany.trim().length > 0);
  const hasName = !!(
    data.policyholderFirstName &&
    data.policyholderFirstName.trim().length > 0 &&
    data.policyholderLastName &&
    data.policyholderLastName.trim().length > 0
  );
  if (data.claimType === 'commercial') {
    return hasCompany || hasName;
  }
  // Residential must have the individual's first and last name.
  return hasName;
}, {
  message: "A business/company name or the policyholder's first and last name is required.",
  path: ["claimType"], // Error will be associated with claimType field
});


export const insertContactSchema = createInsertSchema(contacts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});


export const insertExtractedContactSchema = createInsertSchema(extractedContacts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});


export const insertExtractedDeadlineSchema = createInsertSchema(extractedDeadlines).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});


export const insertContractorSchema = createInsertSchema(contractors).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});


export const insertClaimAdjusterSchema = createInsertSchema(claimAdjusters).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  assignedAt: true,
  acceptedAt: true,
  declinedAt: true,
});


export const insertAiReviewSchema = createInsertSchema(aiReviews).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  generatedAt: true,
  acknowledgedAt: true,
});


export const insertDocumentSchema = createInsertSchema(documents).omit({
  id: true,
  createdAt: true,
});


export const insertCommunicationSchema = createInsertSchema(communications).omit({
  id: true,
  createdAt: true,
});


export const insertAiDraftSchema = createInsertSchema(aiDrafts).omit({
  id: true,
  createdAt: true,
  approvedAt: true,
});


export const insertEmailDraftSchema = createInsertSchema(emailDrafts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  reviewedAt: true,
  sentAt: true,
});


export const insertEmailAccountSchema = createInsertSchema(emailAccounts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});


// Insert schemas for new tables
export const insertCarrierSchema = createInsertSchema(carriers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});


export const insertRoleSchema = createInsertSchema(roles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});


export const insertPermissionSchema = createInsertSchema(permissions).omit({
  id: true,
  createdAt: true,
});


export const insertRolePermissionSchema = createInsertSchema(rolePermissions).omit({
  createdAt: true,
});


export const insertUserPermissionOverrideSchema = createInsertSchema(userPermissionOverrides).omit({
  createdAt: true,
  updatedAt: true,
});


export const insertEstimateSchema = createInsertSchema(estimates).omit({
  id: true,
  createdAt: true,
}).extend({
  // Coerce numeric values to strings for decimal fields
  amount: z.union([z.string(), z.number()]).transform(val => String(val)),
  totalRcv: z.union([z.string(), z.number()]).transform(val => String(val)).optional().nullable(),
  totalRecoverableDepreciation: z.union([z.string(), z.number()]).transform(val => String(val)).optional().nullable(),
  totalNonRecoverableDepreciation: z.union([z.string(), z.number()]).transform(val => String(val)).optional().nullable(),
  totalAcv: z.union([z.string(), z.number()]).transform(val => String(val)).optional().nullable(),
  dwellingRcv: z.union([z.string(), z.number()]).transform(val => String(val)).optional().nullable(),
  dwellingRecoverableDepreciation: z.union([z.string(), z.number()]).transform(val => String(val)).optional().nullable(),
  dwellingNonRecoverableDepreciation: z.union([z.string(), z.number()]).transform(val => String(val)).optional().nullable(),
  otherStructuresRcv: z.union([z.string(), z.number()]).transform(val => String(val)).optional().nullable(),
  otherStructuresRecoverableDepreciation: z.union([z.string(), z.number()]).transform(val => String(val)).optional().nullable(),
  otherStructuresNonRecoverableDepreciation: z.union([z.string(), z.number()]).transform(val => String(val)).optional().nullable(),
  personalPropertyRcv: z.union([z.string(), z.number()]).transform(val => String(val)).optional().nullable(),
  personalPropertyRecoverableDepreciation: z.union([z.string(), z.number()]).transform(val => String(val)).optional().nullable(),
  personalPropertyNonRecoverableDepreciation: z.union([z.string(), z.number()]).transform(val => String(val)).optional().nullable(),
  lossOfUseRcv: z.union([z.string(), z.number()]).transform(val => String(val)).optional().nullable(),
  extractionConfidence: z.union([z.string(), z.number()]).transform(val => String(val)).optional().nullable(),
  // Coerce date strings to Date objects
  receivedDate: z.union([z.string(), z.date()]).transform(val => val ? (typeof val === 'string' ? new Date(val) : val) : undefined).optional().nullable(),
});


export const insertOfferSchema = createInsertSchema(carrierOffers).omit({
  id: true,
  createdAt: true,
});


export const insertPaymentSchema = createInsertSchema(payments).omit({
  id: true,
  createdAt: true,
});


export const insertInvoiceSchema = createInsertSchema(invoices).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});


export const insertClaimPaAssignmentSchema = createInsertSchema(claimPaAssignments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});


export const insertLeadPaAssignmentSchema = createInsertSchema(leadPaAssignments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});


export const insertCommissionSchema = createInsertSchema(commissions).omit({
  id: true,
  createdAt: true,
});


export const insertCommissionPaymentSchema = createInsertSchema(commissionPayments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  // Coerce date strings to Date objects for HTML date inputs
  paymentDate: z.union([z.string(), z.date()]).transform(val => typeof val === 'string' ? new Date(val) : val),
  // Coerce numeric values to strings for decimal fields
  totalAmount: z.union([z.string(), z.number()]).transform(val => String(val)),
});


// Financial Management Insert Schemas
export const insertCarrierOfferSchema = createInsertSchema(carrierOffers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  // Coerce date strings to Date objects for HTML date inputs
  offerDate: z.union([z.string(), z.date()]).transform(val => typeof val === 'string' ? new Date(val) : val),
  settlementDate: z.union([z.string(), z.date()]).transform(val => val ? (typeof val === 'string' ? new Date(val) : val) : undefined).optional(),
});


export const insertCarrierCheckSchema = createInsertSchema(carrierChecks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  // Coerce date strings to Date objects for HTML date inputs
  dateIssued: z.union([z.string(), z.date()]).transform(val => val ? (typeof val === 'string' ? new Date(val) : val) : undefined).optional(),
  dateReceived: z.union([z.string(), z.date()]).transform(val => val ? (typeof val === 'string' ? new Date(val) : val) : undefined).optional(),
  priorReceivedDate: z.union([z.string(), z.date()]).transform(val => val ? (typeof val === 'string' ? new Date(val) : val) : undefined).optional(),
  paymentClearedAt: z.union([z.string(), z.date()]).transform(val => val ? (typeof val === 'string' ? new Date(val) : val) : undefined).optional().nullable(),
  qboSyncedAt: z.union([z.string(), z.date()]).transform(val => val ? (typeof val === 'string' ? new Date(val) : val) : undefined).optional(),
});


export const insertPaymentCoverageBreakdownSchema = createInsertSchema(paymentCoverageBreakdowns).omit({
  id: true,
  createdAt: true,
});

export const insertCheckEstimateBreakdownSchema = createInsertSchema(checkEstimateBreakdowns).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});


export const insertInvoiceLineItemSchema = createInsertSchema(invoiceLineItems).omit({
  id: true,
  createdAt: true,
});


export const insertReceivableFeeSchema = createInsertSchema(receivableFees).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});


export const insertCompanyExpenseSchema = createInsertSchema(companyExpenses).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  // Coerce date strings to Date objects for HTML date inputs
  expenseDate: z.union([z.string(), z.date()]).transform(val => typeof val === 'string' ? new Date(val) : val),
  dueDate: z.union([z.string(), z.date()]).transform(val => val ? (typeof val === 'string' ? new Date(val) : val) : undefined).optional(),
  paidDate: z.union([z.string(), z.date()]).transform(val => val ? (typeof val === 'string' ? new Date(val) : val) : undefined).optional(),
});


export const insertStaffTimeSchema = createInsertSchema(staffTime).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});


export const insertTaskSchema = createInsertSchema(tasks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  // Coerce date strings to Date objects for HTML date inputs
  dueAt: z.union([z.string(), z.date()]).transform(val => val ? (typeof val === 'string' ? new Date(val) : val) : undefined).optional(),
  // date-only field — stored as YYYY-MM-DD string, no Date transform needed
  startAt: z.string().date().optional().nullable(),
});


export const insertTimelineEventSchema = createInsertSchema(timelineEvents).omit({
  id: true,
  createdAt: true,
});


export const insertAttorneyExclusionSchema = createInsertSchema(attorneyExclusions).omit({
  id: true,
  createdAt: true,
});


export const insertLeadSchema = createInsertSchema(leads).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  // Allow dateOfLoss to be optional date-only string
  dateOfLoss: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD").nullable().optional(),
}).refine((data) => {
  // Conditional validation based on claimType
  if (data.claimType === 'commercial') {
    // Commercial leads must have company name
    return data.company && data.company.trim().length > 0;
  } else {
    // Residential leads must have first and last name
    return (
      data.firstName && 
      data.firstName.trim().length > 0 &&
      data.lastName && 
      data.lastName.trim().length > 0
    );
  }
}, {
  message: "For residential leads, first and last name are required. For commercial leads, company name is required.",
  path: ["claimType"], // Error will be associated with claimType field
});


// Types
export type UpsertUser = typeof users.$inferInsert;

export type User = typeof users.$inferSelect & {
  roleType?: string;  // Populated at runtime when role is a UUID
  roleName?: string;  // Populated at runtime when role is a UUID
  membership?: {
    orgId: string | null;
    isOrgAdmin: boolean;
    canManageBilling: boolean;
    canAccessAccounting: boolean;
    hasClaimsAccess: boolean;
  };
  firmBilling?: {
    billingStatus: string;
    cardSaved: boolean;
    cardRequired: boolean;
    cardGate: 'open' | 'card_required' | 'billing_unavailable';
    createdViaBetaSignup: boolean;
    locked: boolean;
    inGracePeriod: boolean;
    trialEndsAt: string | null;
    trialStartAt: string | null;
    firstChargeAt: string | null;
    paidUserCount: number;
    monthlyTotalCents: number;
    showBetaCardBanner: boolean;
    showPaymentFailedNotice: boolean;
  };
};

export type InsertUserLoginLog = typeof userLoginLogs.$inferInsert;

export type UserLoginLog = typeof userLoginLogs.$inferSelect;

export type InsertClaim = z.infer<typeof insertClaimSchema>;

export type Claim = typeof claims.$inferSelect;

export type InsertContact = z.infer<typeof insertContactSchema>;

export type Contact = typeof contacts.$inferSelect;

export type InsertExtractedContact = z.infer<typeof insertExtractedContactSchema>;

export type ExtractedContact = typeof extractedContacts.$inferSelect;

export type InsertExtractedDeadline = z.infer<typeof insertExtractedDeadlineSchema>;

export type ExtractedDeadline = typeof extractedDeadlines.$inferSelect;

export type InsertContractor = z.infer<typeof insertContractorSchema>;

export type Contractor = typeof contractors.$inferSelect;

export type InsertClaimAdjuster = z.infer<typeof insertClaimAdjusterSchema>;

export type ClaimAdjuster = typeof claimAdjusters.$inferSelect;

export type InsertAiReview = z.infer<typeof insertAiReviewSchema>;

export type AiReview = typeof aiReviews.$inferSelect;


// Continuous Learning Types
export const insertReviewFeedbackSchema = createInsertSchema(reviewFeedback).omit({
  id: true,
  createdAt: true,
});

export type InsertReviewFeedback = z.infer<typeof insertReviewFeedbackSchema>;

export type ReviewFeedback = typeof reviewFeedback.$inferSelect;


export const insertStrategyOutcomeSchema = createInsertSchema(strategyOutcomes).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertStrategyOutcome = z.infer<typeof insertStrategyOutcomeSchema>;

export type StrategyOutcome = typeof strategyOutcomes.$inferSelect;


export const insertLearningExampleSchema = createInsertSchema(learningExamples).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertLearningExample = z.infer<typeof insertLearningExampleSchema>;

export type LearningExample = typeof learningExamples.$inferSelect;


// AI Agent System Types
export const insertAgentRunSchema = createInsertSchema(agentRuns).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertAgentRun = z.infer<typeof insertAgentRunSchema>;

export type AgentRun = typeof agentRuns.$inferSelect;


export const insertAgentActionSchema = createInsertSchema(agentActions).omit({
  id: true,
  createdAt: true,
});

export type InsertAgentAction = z.infer<typeof insertAgentActionSchema>;

export type AgentAction = typeof agentActions.$inferSelect;


export const insertAgentConfigurationSchema = createInsertSchema(agentConfigurations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertAgentConfiguration = z.infer<typeof insertAgentConfigurationSchema>;

export type AgentConfiguration = typeof agentConfigurations.$inferSelect;


export type InsertDocument = z.infer<typeof insertDocumentSchema>;

export type Document = typeof documents.$inferSelect;

export type InsertCommunication = z.infer<typeof insertCommunicationSchema>;

export type Communication = typeof communications.$inferSelect;

export type InsertAiDraft = z.infer<typeof insertAiDraftSchema>;

export type AiDraft = typeof aiDrafts.$inferSelect;

export type InsertEmailDraft = z.infer<typeof insertEmailDraftSchema>;

export type EmailDraft = typeof emailDrafts.$inferSelect;

export type InsertEmailAccount = z.infer<typeof insertEmailAccountSchema>;

export type EmailAccount = typeof emailAccounts.$inferSelect;

export type AuditLog = typeof auditLogs.$inferSelect;


// Enhanced audit log schemas
export const insertAuditLogSchema = createInsertSchema(auditLogs).omit({
  id: true,
  createdAt: true,
});


export type InsertAuditLogType = z.infer<typeof insertAuditLogSchema>;


export type AuditAction = typeof AUDIT_ACTIONS[keyof typeof AUDIT_ACTIONS];


export type AuditEntity = typeof AUDIT_ENTITIES[keyof typeof AUDIT_ENTITIES];


// Types for new tables
export type InsertCarrier = z.infer<typeof insertCarrierSchema>;

export type Carrier = typeof carriers.$inferSelect;

export type InsertRole = z.infer<typeof insertRoleSchema>;

export type Role = typeof roles.$inferSelect;

export type InsertPermission = z.infer<typeof insertPermissionSchema>;

export type Permission = typeof permissions.$inferSelect;

export type InsertRolePermission = z.infer<typeof insertRolePermissionSchema>;

export type RolePermission = typeof rolePermissions.$inferSelect;

export type InsertUserPermissionOverride = z.infer<typeof insertUserPermissionOverrideSchema>;

export type UserPermissionOverride = typeof userPermissionOverrides.$inferSelect;

export type InsertEstimate = z.infer<typeof insertEstimateSchema>;

export type Estimate = typeof estimates.$inferSelect;

// Legacy offer types - replaced by CarrierOffer
// export type InsertOffer = z.infer<typeof insertOfferSchema>;
// export type Offer = typeof offers.$inferSelect;
export type InsertPayment = z.infer<typeof insertPaymentSchema>;

export type Payment = typeof payments.$inferSelect;

export type InsertInvoice = z.infer<typeof insertInvoiceSchema>;

export type Invoice = typeof invoices.$inferSelect;

export type InsertClaimPaAssignment = z.infer<typeof insertClaimPaAssignmentSchema>;

export type ClaimPaAssignment = typeof claimPaAssignments.$inferSelect;

export type InsertLeadPaAssignment = z.infer<typeof insertLeadPaAssignmentSchema>;

export type LeadPaAssignment = typeof leadPaAssignments.$inferSelect;

export type InsertCommission = z.infer<typeof insertCommissionSchema>;

export type Commission = typeof commissions.$inferSelect;

export type InsertCommissionPayment = z.infer<typeof insertCommissionPaymentSchema>;

export type CommissionPayment = typeof commissionPayments.$inferSelect;

export type InsertTask = z.infer<typeof insertTaskSchema>;

export type Task = typeof tasks.$inferSelect;

export const insertTaskAssigneeSchema = createInsertSchema(taskAssignees).omit({
  id: true,
  createdAt: true,
});
export type InsertTaskAssignee = z.infer<typeof insertTaskAssigneeSchema>;
export type TaskAssignee = typeof taskAssignees.$inferSelect;

export type InsertTimelineEvent = z.infer<typeof insertTimelineEventSchema>;

export type TimelineEvent = typeof timelineEvents.$inferSelect;

export type InsertAttorneyExclusion = z.infer<typeof insertAttorneyExclusionSchema>;

export type AttorneyExclusion = typeof attorneyExclusions.$inferSelect;

export type InsertLead = z.infer<typeof insertLeadSchema>;

export type Lead = typeof leads.$inferSelect;


// Financial Management Types
export type InsertCarrierOffer = z.infer<typeof insertCarrierOfferSchema>;

export type CarrierOffer = typeof carrierOffers.$inferSelect;

export type InsertCarrierCheck = z.infer<typeof insertCarrierCheckSchema>;

export type CarrierCheck = typeof carrierChecks.$inferSelect;

export type InsertPaymentCoverageBreakdown = z.infer<typeof insertPaymentCoverageBreakdownSchema>;

export type PaymentCoverageBreakdown = typeof paymentCoverageBreakdowns.$inferSelect;

export type InsertCheckEstimateBreakdown = z.infer<typeof insertCheckEstimateBreakdownSchema>;

export type CheckEstimateBreakdown = typeof checkEstimateBreakdowns.$inferSelect;

export type InsertInvoiceLineItem = z.infer<typeof insertInvoiceLineItemSchema>;

export type InvoiceLineItem = typeof invoiceLineItems.$inferSelect;

export type InsertReceivableFee = z.infer<typeof insertReceivableFeeSchema>;

export type ReceivableFee = typeof receivableFees.$inferSelect;

export type InsertCompanyExpense = z.infer<typeof insertCompanyExpenseSchema>;

export type CompanyExpense = typeof companyExpenses.$inferSelect;

export type InsertStaffTime = z.infer<typeof insertStaffTimeSchema>;

export type StaffTime = typeof staffTime.$inferSelect;


// Claims Evaluation schemas
export const insertClaimsEvaluationSchema = createInsertSchema(claimsEvaluations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  evaluatedAt: true,
});

export type InsertClaimsEvaluation = z.infer<typeof insertClaimsEvaluationSchema>;

export type ClaimsEvaluation = typeof claimsEvaluations.$inferSelect;


// Intake Decision schemas
export const insertIntakeDecisionSchema = createInsertSchema(intakeDecisions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertIntakeDecision = z.infer<typeof insertIntakeDecisionSchema>;

export type IntakeDecision = typeof intakeDecisions.$inferSelect;


// State License schemas
export const insertStateLicenseSchema = createInsertSchema(stateLicenses).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertStateLicense = z.infer<typeof insertStateLicenseSchema>;

export type StateLicense = typeof stateLicenses.$inferSelect;


// Automated Communication schemas
export const insertAutomatedCommunicationSchema = createInsertSchema(automatedCommunications).omit({
  id: true,
  createdAt: true,
});

export type InsertAutomatedCommunication = z.infer<typeof insertAutomatedCommunicationSchema>;

export type AutomatedCommunication = typeof automatedCommunications.$inferSelect;


// Appraisal Workflow schemas
export const insertAppraisalDecisionSchema = createInsertSchema(appraisalDecisions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertAppraisalDecision = z.infer<typeof insertAppraisalDecisionSchema>;

export type AppraisalDecision = typeof appraisalDecisions.$inferSelect;


export const insertAppraisalAgreementSchema = createInsertSchema(appraisalAgreements).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertAppraisalAgreement = z.infer<typeof insertAppraisalAgreementSchema>;

export type AppraisalAgreement = typeof appraisalAgreements.$inferSelect;


export const insertAppraisalTrackingSchema = createInsertSchema(appraisalTracking).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertAppraisalTracking = z.infer<typeof insertAppraisalTrackingSchema>;

export type AppraisalTracking = typeof appraisalTracking.$inferSelect;


export const insertAppraisalWorkflowDocumentSchema = createInsertSchema(appraisalWorkflowDocuments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertAppraisalWorkflowDocument = z.infer<typeof insertAppraisalWorkflowDocumentSchema>;

export type AppraisalWorkflowDocument = typeof appraisalWorkflowDocuments.$inferSelect;


// Policy Analysis schemas
export const insertPolicyAnalysisSchema = createInsertSchema(policyAnalysis).omit({
  id: true,
  createdAt: true,
  analyzedAt: true,
});

export type InsertPolicyAnalysis = z.infer<typeof insertPolicyAnalysisSchema>;

export type PolicyAnalysis = typeof policyAnalysis.$inferSelect;


// Policyholder Update schemas
export const insertPolicyholderUpdateSchema = createInsertSchema(policyholderUpdates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertPolicyholderUpdate = z.infer<typeof insertPolicyholderUpdateSchema>;

export type PolicyholderUpdate = typeof policyholderUpdates.$inferSelect;


// Inspection schemas
export const insertInspectionSchema = createInsertSchema(inspections).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertInspection = z.infer<typeof insertInspectionSchema>;

export type Inspection = typeof inspections.$inferSelect;


// Document Template schemas
export const insertDocumentTemplateSchema = createInsertSchema(documentTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertDocumentTemplate = z.infer<typeof insertDocumentTemplateSchema>;

export type DocumentTemplate = typeof documentTemplates.$inferSelect;


// Document Generation schemas
export const insertDocumentGenerationSchema = createInsertSchema(documentGenerations).omit({
  id: true,
  generatedAt: true,
  updatedAt: true,
});

export type InsertDocumentGeneration = z.infer<typeof insertDocumentGenerationSchema>;

export type DocumentGeneration = typeof documentGenerations.$inferSelect;


// DocuSign Envelope schemas
export const insertDocusignEnvelopeSchema = createInsertSchema(docusignEnvelopes).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertDocusignEnvelope = z.infer<typeof insertDocusignEnvelopeSchema>;

export type DocusignEnvelope = typeof docusignEnvelopes.$inferSelect;


// Token Definition schemas
export const insertTokenDefinitionSchema = createInsertSchema(tokenDefinitions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertTokenDefinition = z.infer<typeof insertTokenDefinitionSchema>;

export type TokenDefinition = typeof tokenDefinitions.$inferSelect;


// Document Template Package schemas
export const insertDocumentTemplatePackageSchema = createInsertSchema(documentTemplatePackages).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertDocumentTemplatePackage = z.infer<typeof insertDocumentTemplatePackageSchema>;

export type DocumentTemplatePackage = typeof documentTemplatePackages.$inferSelect;


// Package Template schemas
export const insertPackageTemplateSchema = createInsertSchema(packageTemplates).omit({
  id: true,
  createdAt: true,
});

export type InsertPackageTemplate = z.infer<typeof insertPackageTemplateSchema>;

export type PackageTemplate = typeof packageTemplates.$inferSelect;


// Lead Feedback schemas
export const insertLeadFeedbackSchema = createInsertSchema(leadFeedbacks).omit({
  id: true,
  recordedAt: true,
});

export type InsertLeadFeedback = z.infer<typeof insertLeadFeedbackSchema>;

export type LeadFeedback = typeof leadFeedbacks.$inferSelect;


export const insertQuestionnaireTemplateSchema = createInsertSchema(questionnaireTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});

export type InsertQuestionnaireTemplate = z.infer<typeof insertQuestionnaireTemplateSchema>;

export type QuestionnaireTemplate = typeof questionnaireTemplates.$inferSelect;


export const insertQuestionnaireResponseSchema = createInsertSchema(questionnaireResponses).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  accessToken: true
});

export type InsertQuestionnaireResponse = z.infer<typeof insertQuestionnaireResponseSchema>;

export type QuestionnaireResponse = typeof questionnaireResponses.$inferSelect;


// Export schemas and types for the reminder system
export const insertReminderConfigurationSchema = createInsertSchema(reminderConfigurations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertReminderConfiguration = z.infer<typeof insertReminderConfigurationSchema>;

export type ReminderConfiguration = typeof reminderConfigurations.$inferSelect;


export const insertReminderHistorySchema = createInsertSchema(reminderHistory).omit({
  id: true,
  createdAt: true,
});

export type InsertReminderHistory = z.infer<typeof insertReminderHistorySchema>;

export type ReminderHistory = typeof reminderHistory.$inferSelect;


export const insertCompletionDocumentSchema = createInsertSchema(completionDocuments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertCompletionDocument = z.infer<typeof insertCompletionDocumentSchema>;

export type CompletionDocument = typeof completionDocuments.$inferSelect;


export const insertEmailTemplateSchema = createInsertSchema(emailTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertEmailTemplate = z.infer<typeof insertEmailTemplateSchema>;

export type EmailTemplate = typeof emailTemplates.$inferSelect;


export const insertEmailCommunicationSchema = createInsertSchema(emailCommunications).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertEmailCommunication = z.infer<typeof insertEmailCommunicationSchema>;

export type EmailCommunication = typeof emailCommunications.$inferSelect;


// Export schemas and types for claim coverages
export const insertClaimCoverageSchema = createInsertSchema(claimCoverages).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  acv: true, // ACV is calculated, not input
});

export type InsertClaimCoverage = z.infer<typeof insertClaimCoverageSchema>;

export type ClaimCoverage = typeof claimCoverages.$inferSelect;


// Export schemas and types for AI features
export const insertDamageAssessmentSchema = createInsertSchema(damageAssessments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertDamageAssessment = z.infer<typeof insertDamageAssessmentSchema>;

export type DamageAssessment = typeof damageAssessments.$inferSelect;


export const insertSettlementPredictionSchema = createInsertSchema(settlementPredictions).omit({
  id: true,
  createdAt: true,
});

export type InsertSettlementPrediction = z.infer<typeof insertSettlementPredictionSchema>;

export type SettlementPrediction = typeof settlementPredictions.$inferSelect;


export const insertCommunicationSummarySchema = createInsertSchema(communicationSummaries).omit({
  id: true,
  createdAt: true,
});

export type InsertCommunicationSummary = z.infer<typeof insertCommunicationSummarySchema>;

export type CommunicationSummary = typeof communicationSummaries.$inferSelect;


export const insertSearchEmbeddingSchema = createInsertSchema(searchEmbeddings).omit({
  id: true,
  createdAt: true,
});

export type InsertSearchEmbedding = z.infer<typeof insertSearchEmbeddingSchema>;

export type SearchEmbedding = typeof searchEmbeddings.$inferSelect;


export const insertSmartSuggestionSchema = createInsertSchema(smartSuggestions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertSmartSuggestion = z.infer<typeof insertSmartSuggestionSchema>;

export type SmartSuggestion = typeof smartSuggestions.$inferSelect;


export const insertAiFeedbackSchema = createInsertSchema(aiFeedback).omit({
  id: true,
  createdAt: true,
});

export type InsertAiFeedback = z.infer<typeof insertAiFeedbackSchema>;

export type AiFeedback = typeof aiFeedback.$inferSelect;


export const insertTrainingDataSchema = createInsertSchema(trainingData).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertTrainingData = z.infer<typeof insertTrainingDataSchema>;

export type TrainingData = typeof trainingData.$inferSelect;


export const insertFineTuningJobSchema = createInsertSchema(fineTuningJobs).omit({
  id: true,
  createdAt: true,
});

export type InsertFineTuningJob = z.infer<typeof insertFineTuningJobSchema>;

export type FineTuningJob = typeof fineTuningJobs.$inferSelect;


// File Type Management Insert Schemas
export const insertExternalAppraiserSchema = createInsertSchema(externalAppraisers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertExternalAppraiser = z.infer<typeof insertExternalAppraiserSchema>;

export type ExternalAppraiser = typeof externalAppraisers.$inferSelect;


export const insertAppraisalAssignmentSchema = createInsertSchema(appraisalAssignments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertAppraisalAssignment = z.infer<typeof insertAppraisalAssignmentSchema>;

export type AppraisalAssignment = typeof appraisalAssignments.$inferSelect;


// Estimate Learning Library Insert Schemas
export const insertEstimatePitfallPatternSchema = createInsertSchema(estimatePitfallPatterns).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertEstimatePitfallPattern = z.infer<typeof insertEstimatePitfallPatternSchema>;

export type EstimatePitfallPattern = typeof estimatePitfallPatterns.$inferSelect;


export const insertEstimateSampleSchema = createInsertSchema(estimateSamples).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertEstimateSample = z.infer<typeof insertEstimateSampleSchema>;

export type EstimateSample = typeof estimateSamples.$inferSelect;


export const insertDiscrepancyAnnotationSchema = createInsertSchema(discrepancyAnnotations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertDiscrepancyAnnotation = z.infer<typeof insertDiscrepancyAnnotationSchema>;

export type DiscrepancyAnnotation = typeof discrepancyAnnotations.$inferSelect;


// Vertex AI Training Dataset Insert Schemas
export const insertDamageTrainingDatasetSchema = createInsertSchema(damageTrainingDataset).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertDamageTrainingDataset = z.infer<typeof insertDamageTrainingDatasetSchema>;

export type DamageTrainingDataset = typeof damageTrainingDataset.$inferSelect;


export const insertVertexAiModelSchema = createInsertSchema(vertexAiModels).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertVertexAiModel = z.infer<typeof insertVertexAiModelSchema>;

export type VertexAiModel = typeof vertexAiModels.$inferSelect;


// AI Training Hub Insert Schemas
export const insertAiDocumentExampleSchema = createInsertSchema(aiDocumentExamples).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertAiDocumentExample = z.infer<typeof insertAiDocumentExampleSchema>;

export type AiDocumentExample = typeof aiDocumentExamples.$inferSelect;


export const insertPolicyClassificationTrainingSchema = createInsertSchema(policyClassificationTraining).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertPolicyClassificationTraining = z.infer<typeof insertPolicyClassificationTrainingSchema>;

export type PolicyClassificationTraining = typeof policyClassificationTraining.$inferSelect;


export const insertStateStatuteSchema = createInsertSchema(stateStatutes).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertStateStatute = z.infer<typeof insertStateStatuteSchema>;

export type StateStatute = typeof stateStatutes.$inferSelect;


// Insert Schemas for Chat
export const insertChatSpaceSchema = createInsertSchema(chatSpaces).omit({
  createdAt: true,
  updatedAt: true,
});

export type InsertChatSpace = z.infer<typeof insertChatSpaceSchema>;

export type ChatSpace = typeof chatSpaces.$inferSelect;


export const insertChatMessageSchema = createInsertSchema(chatMessages).omit({
  createdAt: true,
});

export type InsertChatMessage = z.infer<typeof insertChatMessageSchema>;

export type ChatMessage = typeof chatMessages.$inferSelect;


export const insertChatParticipantSchema = createInsertSchema(chatParticipants).omit({
  id: true,
  joinedAt: true,
});

export type InsertChatParticipant = z.infer<typeof insertChatParticipantSchema>;

export type ChatParticipant = typeof chatParticipants.$inferSelect;


// Chat Agents schemas
export const insertChatAgentSchema = createInsertSchema(chatAgents).omit({
  createdAt: true,
  updatedAt: true,
});

export type InsertChatAgent = z.infer<typeof insertChatAgentSchema>;

export type ChatAgent = typeof chatAgents.$inferSelect;


export type PendingToolCall = typeof pendingToolCalls.$inferSelect;

export type InsertPendingToolCall = typeof pendingToolCalls.$inferInsert;

export const insertChatMessageReactionSchema = createInsertSchema(chatMessageReactions).omit({
  createdAt: true,
});

export type InsertChatMessageReaction = z.infer<typeof insertChatMessageReactionSchema>;
export type ChatMessageReaction = typeof chatMessageReactions.$inferSelect;


// Insert Schemas for GDPR Tables
export const insertRetentionPolicySchema = createInsertSchema(retentionPolicies).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertRetentionPolicy = z.infer<typeof insertRetentionPolicySchema>;

export type RetentionPolicy = typeof retentionPolicies.$inferSelect;


export const insertDataDeletionRequestSchema = createInsertSchema(dataDeletionRequests).omit({
  id: true,
  createdAt: true,
});

export type InsertDataDeletionRequest = z.infer<typeof insertDataDeletionRequestSchema>;

export type DataDeletionRequest = typeof dataDeletionRequests.$inferSelect;


export const insertBreachNotificationSchema = createInsertSchema(breachNotifications).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertBreachNotification = z.infer<typeof insertBreachNotificationSchema>;

export type BreachNotification = typeof breachNotifications.$inferSelect;


// Insert Schema for Activity Tracking
export const insertClaimActivityTrackingSchema = createInsertSchema(claimActivityTracking).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertClaimActivityTracking = z.infer<typeof insertClaimActivityTrackingSchema>;

export type ClaimActivityTracking = typeof claimActivityTracking.$inferSelect;


// Insert Schemas for new tables
export const insertPackageVersionSchema = createInsertSchema(packageVersions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertPackageVersion = z.infer<typeof insertPackageVersionSchema>;

export type PackageVersion = typeof packageVersions.$inferSelect;


export const insertFeeConfirmationSchema = createInsertSchema(feeConfirmations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertFeeConfirmation = z.infer<typeof insertFeeConfirmationSchema>;

export type FeeConfirmation = typeof feeConfirmations.$inferSelect;


export const insertClaimPackageGenerationSchema = createInsertSchema(claimPackageGenerations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertClaimPackageGeneration = z.infer<typeof insertClaimPackageGenerationSchema>;

export type ClaimPackageGeneration = typeof claimPackageGenerations.$inferSelect;


export const insertPackageValidationRuleSchema = createInsertSchema(packageValidationRules).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertPackageValidationRule = z.infer<typeof insertPackageValidationRuleSchema>;

export type PackageValidationRule = typeof packageValidationRules.$inferSelect;


// AI Training Capture System - Insert Schemas and Types
export const insertAiInteractionLogSchema = createInsertSchema(aiInteractionLogs).omit({
  id: true,
  createdAt: true,
});

export type InsertAiInteractionLog = z.infer<typeof insertAiInteractionLogSchema>;

export type AiInteractionLog = typeof aiInteractionLogs.$inferSelect;


export const insertAiTrainingFeedbackSchema = createInsertSchema(aiTrainingFeedback).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertAiTrainingFeedback = z.infer<typeof insertAiTrainingFeedbackSchema>;

export type AiTrainingFeedback = typeof aiTrainingFeedback.$inferSelect;


export const insertSearchAnalyticsSchema = createInsertSchema(searchAnalytics).omit({
  id: true,
  createdAt: true,
});

export type InsertSearchAnalytics = z.infer<typeof insertSearchAnalyticsSchema>;

export type SearchAnalytics = typeof searchAnalytics.$inferSelect;


export const insertAiDraftEditSchema = createInsertSchema(aiDraftEdits).omit({
  id: true,
  createdAt: true,
});

export type InsertAiDraftEdit = z.infer<typeof insertAiDraftEditSchema>;

export type AiDraftEdit = typeof aiDraftEdits.$inferSelect;


export const insertTimelineNotePatternSchema = createInsertSchema(timelineNotePatterns).omit({
  id: true,
  createdAt: true,
});

export type InsertTimelineNotePattern = z.infer<typeof insertTimelineNotePatternSchema>;

export type TimelineNotePattern = typeof timelineNotePatterns.$inferSelect;


export const insertDocumentContentLearningSchema = createInsertSchema(documentContentLearning).omit({
  id: true,
  createdAt: true,
});

export type InsertDocumentContentLearning = z.infer<typeof insertDocumentContentLearningSchema>;

export type DocumentContentLearning = typeof documentContentLearning.$inferSelect;


export const insertEstimateComponentGroupSchema = createInsertSchema(estimateComponentGroups).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertEstimateComponentGroup = z.infer<typeof insertEstimateComponentGroupSchema>;

export type EstimateComponentGroup = typeof estimateComponentGroups.$inferSelect;


export const insertEstimateItemObservationSchema = createInsertSchema(estimateItemObservations).omit({
  id: true,
  createdAt: true,
});

export type InsertEstimateItemObservation = z.infer<typeof insertEstimateItemObservationSchema>;

export type EstimateItemObservation = typeof estimateItemObservations.$inferSelect;


export const insertTrainingExportJobSchema = createInsertSchema(trainingExportJobs).omit({
  id: true,
  createdAt: true,
});

export type InsertTrainingExportJob = z.infer<typeof insertTrainingExportJobSchema>;

export type TrainingExportJob = typeof trainingExportJobs.$inferSelect;


// Twilio Call System - Insert Schemas and Types
export const insertCompanyPhoneLineSchema = createInsertSchema(companyPhoneLines).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertCompanyPhoneLine = z.infer<typeof insertCompanyPhoneLineSchema>;

export type CompanyPhoneLine = typeof companyPhoneLines.$inferSelect;


export const insertCallRecordSchema = createInsertSchema(callRecords).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertCallRecord = z.infer<typeof insertCallRecordSchema>;

export type CallRecord = typeof callRecords.$inferSelect;


// Legal Compliance Tracker - Insert Schemas and Types
export const insertStateComplianceRuleSchema = createInsertSchema(stateComplianceRules).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertStateComplianceRule = z.infer<typeof insertStateComplianceRuleSchema>;

export type StateComplianceRule = typeof stateComplianceRules.$inferSelect;


export const insertClaimComplianceItemSchema = createInsertSchema(claimComplianceItems).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertClaimComplianceItem = z.infer<typeof insertClaimComplianceItemSchema>;

export type ClaimComplianceItem = typeof claimComplianceItems.$inferSelect;


// Saved Reports - Insert Schemas and Types
export const insertSavedReportSchema = createInsertSchema(savedReports).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertSavedReport = z.infer<typeof insertSavedReportSchema>;

export type SavedReport = typeof savedReports.$inferSelect;


// AI Suggestion Feedback - Insert Schema and Types
export const insertAiSuggestionFeedbackSchema = createInsertSchema(aiSuggestionFeedback).omit({
  id: true,
  createdAt: true,
});

export type InsertAiSuggestionFeedback = z.infer<typeof insertAiSuggestionFeedbackSchema>;

export type AiSuggestionFeedback = typeof aiSuggestionFeedback.$inferSelect;


// QuickBooks Integration - Insert Schemas and Types
export const insertQuickbooksTokensSchema = createInsertSchema(quickbooksTokens).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertQuickbooksTokens = z.infer<typeof insertQuickbooksTokensSchema>;

export type QuickbooksTokens = typeof quickbooksTokens.$inferSelect;


export const insertQuickbooksSyncLogSchema = createInsertSchema(quickbooksSyncLog).omit({
  id: true,
  createdAt: true,
});

export type InsertQuickbooksSyncLog = z.infer<typeof insertQuickbooksSyncLogSchema>;

export type QuickbooksSyncLog = typeof quickbooksSyncLog.$inferSelect;


export const insertQuickbooksAccountMappingsSchema = createInsertSchema(quickbooksAccountMappings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertQuickbooksAccountMappings = z.infer<typeof insertQuickbooksAccountMappingsSchema>;

export type QuickbooksAccountMappings = typeof quickbooksAccountMappings.$inferSelect;


// Claim Photos - Insert Schema and Types
export const insertClaimPhotoSchema = createInsertSchema(claimPhotos).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
});

export type InsertClaimPhoto = z.infer<typeof insertClaimPhotoSchema>;

export type ClaimPhoto = typeof claimPhotos.$inferSelect;


export const insertClaimPhotoCommentSchema = createInsertSchema(claimPhotoComments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertClaimPhotoComment = z.infer<typeof insertClaimPhotoCommentSchema>;

export type ClaimPhotoComment = typeof claimPhotoComments.$inferSelect;


// RAG Tables - Insert Schemas and Types
export const insertDocumentChunkSchema = createInsertSchema(documentChunks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertDocumentChunk = z.infer<typeof insertDocumentChunkSchema>;

export type DocumentChunk = typeof documentChunks.$inferSelect;


export const insertKnowledgeBaseEntrySchema = createInsertSchema(knowledgeBaseEntries).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertKnowledgeBaseEntry = z.infer<typeof insertKnowledgeBaseEntrySchema>;

export type KnowledgeBaseEntry = typeof knowledgeBaseEntries.$inferSelect;


export const insertDamageAnalysisCorrectionSchema = createInsertSchema(damageAnalysisCorrections).omit({
  id: true,
  createdAt: true,
});

export type InsertDamageAnalysisCorrection = z.infer<typeof insertDamageAnalysisCorrectionSchema>;

export type DamageAnalysisCorrection = typeof damageAnalysisCorrections.$inferSelect;


export const insertClaimLinkSchema = createInsertSchema(claimLinks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertClaimLink = z.infer<typeof insertClaimLinkSchema>;

export type ClaimLink = typeof claimLinks.$inferSelect;


export const insertAdjusterPhoneDirectorySchema = createInsertSchema(adjusterPhoneDirectory).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertAdjusterPhoneDirectory = z.infer<typeof insertAdjusterPhoneDirectorySchema>;

export type AdjusterPhoneDirectory = typeof adjusterPhoneDirectory.$inferSelect;


export const insertAiCorrectionSchema = createInsertSchema(aiCorrections).omit({
  id: true,
  createdAt: true,
});

export type InsertAiCorrection = z.infer<typeof insertAiCorrectionSchema>;

export type AiCorrection = typeof aiCorrections.$inferSelect;


export const insertProcessedEmailSchema = createInsertSchema(processedEmails).omit({
  id: true,
  createdAt: true,
});

export type InsertProcessedEmail = z.infer<typeof insertProcessedEmailSchema>;

export type ProcessedEmail = typeof processedEmails.$inferSelect;


export const insertNotificationSchema = createInsertSchema(notifications).omit({
  id: true,
  createdAt: true,
  readAt: true,
});


export type InsertNotification = z.infer<typeof insertNotificationSchema>;

export type Notification = typeof notifications.$inferSelect;

export const insertBackgroundJobSchema = createInsertSchema(backgroundJobs).omit({
  id: true,
  startedAt: true,
  completedAt: true,
});

export type InsertBackgroundJob = z.infer<typeof insertBackgroundJobSchema>;
export type BackgroundJob = typeof backgroundJobs.$inferSelect;

export const insertApiKeySchema = createInsertSchema(apiKeys).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertApiKey = z.infer<typeof insertApiKeySchema>;
export type ApiKey = typeof apiKeys.$inferSelect;

// Import Jobs
export const insertImportJobSchema = createInsertSchema(importJobs).omit({
  id: true,
  createdAt: true,
  completedAt: true,
});
export type InsertImportJob = z.infer<typeof insertImportJobSchema>;
export type ImportJob = typeof importJobs.$inferSelect;

export const insertImportJobItemSchema = createInsertSchema(importJobItems).omit({
  id: true,
  createdAt: true,
});
export type InsertImportJobItem = z.infer<typeof insertImportJobItemSchema>;
export type ImportJobItem = typeof importJobItems.$inferSelect;

export const insertImportJobGroupSchema = createInsertSchema(importJobGroups).omit({
  id: true,
  createdAt: true,
});
export type InsertImportJobGroup = z.infer<typeof insertImportJobGroupSchema>;
export type ImportJobGroup = typeof importJobGroups.$inferSelect;

export const insertEmailAddressHistorySchema = createInsertSchema(emailAddressHistory).omit({ id: true, createdAt: true });
export type InsertEmailAddressHistory = z.infer<typeof insertEmailAddressHistorySchema>;
export type EmailAddressHistory = typeof emailAddressHistory.$inferSelect;

export const insertTimesheetSchema = createInsertSchema(timesheets).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertTimesheet = z.infer<typeof insertTimesheetSchema>;
export type Timesheet = typeof timesheets.$inferSelect;

// OrgUsageLimits types
export const insertOrgUsageLimitsSchema = createInsertSchema(orgUsageLimits).omit({
  id: true,
  updatedAt: true,
});
export type InsertOrgUsageLimits = z.infer<typeof insertOrgUsageLimitsSchema>;
export type OrgUsageLimits = typeof orgUsageLimits.$inferSelect;

// Letterstream / Postal
export const insertLetterstreamJobSchema = createInsertSchema(letterstreamJobs).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertLetterstreamJob = z.infer<typeof insertLetterstreamJobSchema>;
export type LetterstreamJob = typeof letterstreamJobs.$inferSelect;
